import { productLog } from "common/utils/logging";
import type { AABB2 } from "geom/aabb2Types";
import { TransformControls } from "gfx/threejs/examples/jsm/controls/TransformControls";
import type { Camera } from "three";
import {
  Color,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  OrthographicCamera,
  PlaneGeometry,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
  Vector4,
} from "three";
import { initSharedWebGL, WebGLContextReuse } from "./activeSharedWebGL";
import { initPhysicallyBasedRenderer } from "./initPhysicallyBasedRenderer";
import { BakeColorTextureMode } from "./materials/designMaterialRuntimeConfig";
import { prepareMaterialsForRender } from "./materials/prepareMaterialsForRender";
import type { PerformanceTier } from "./performanceTiers";
import { PERFORMANCE_TIERS } from "./performanceTiers";
import type { SharedWebGL } from "./SharedWebGL";
import type { SharedUniformValues } from "./threejs/higharc/MaterialSharedParameterGroup";
import {
  MaterialSharedParameterGroup,
  SOFT_SHADOW_CAMERA_FAR_DEFAULT,
} from "./threejs/higharc/MaterialSharedParameterGroup";
import type { RenderTargetPool } from "./threejs/higharc/RenderTargetPool";
import { LightProbeVisualizationMode } from "./threejs/higharc/SHLightProbeConstants";
import type { ImageReadyCallback } from "./threejs/higharc/SSFXImageRenderer";
import type { SSFXLightweightRenderTarget } from "./threejs/higharc/SSFXLightweightRenderTarget";
import { SSFXRendererOutput } from "./threejs/higharc/SSFXRendererOutput";
import type {
  RendererParams,
  RenderImageOptions,
  SSFXRendererParamsManager,
} from "./threejs/higharc/SSFXRendererParams";
import type SSFXRendererScene from "./threejs/higharc/SSFXRendererScene";
import type { SSFXRenderTarget } from "./threejs/higharc/SSFXRenderTarget";
import Stats from "./threejs/Stats";
import type { ExtendedScene } from "./threejs/three-extensions";
import type { THREEViewerBase } from "./THREEViewerBase";
import type { THREEViewerOptions } from "./THREEViewerOptions";
import { HIGHARC_VIEWER_REINIT_CLASS, THREEViewerRendererBase } from "./THREEViewerRendererBase";
import type { THREEViewerServices } from "./THREEViewerServices";
import { TONE_MAPPING_ALGORITHM } from "./toneMappingConstants";
import { WebGLMultiContextResourceTracker } from "./WebGLContextResourceTracker";

interface EnvironmentHudState {
  enabled: boolean;
  mode: "sphere" | "background";
  size: number;
  padding: number;
  useViewerAngle: boolean;
  yaw: number;
  pitch: number;
  scene: Scene;
  camera: PerspectiveCamera;
  sphereMaterial: MeshStandardMaterial;
  sphere: Mesh;
  cameraDirection: Vector3;
  cameraUp: Vector3;
  oldViewport: Vector4;
  oldScissor: Vector4;
  oldClearColor: Color;
  darkBackground: Color;
}

function createEnvironmentHudState(): EnvironmentHudState {
  const scene = new Scene();
  const camera = new PerspectiveCamera(70, 1, 0.1, 10);
  const sphereMaterial = new MeshStandardMaterial({
    color: 0xffffff,
    metalness: 1,
    roughness: 0,
  });
  const sphere = new Mesh(new SphereGeometry(0.75, 48, 32), sphereMaterial);

  scene.add(sphere);

  return {
    enabled: false,
    mode: "sphere",
    size: 220,
    padding: 12,
    useViewerAngle: true,
    yaw: 35,
    pitch: 10,
    scene,
    camera,
    sphereMaterial,
    sphere,
    cameraDirection: new Vector3(),
    cameraUp: new Vector3(),
    oldViewport: new Vector4(),
    oldScissor: new Vector4(),
    oldClearColor: new Color(),
    darkBackground: new Color(0x111111),
  };
}

interface ShadowMapHudState {
  enabled: boolean;
  size: number;
  padding: number;
  flipY: boolean;
  invert: boolean;
  scene: Scene;
  camera: OrthographicCamera;
  oldViewport: Vector4;
  oldScissor: Vector4;
  oldClearColor: Color;
  material: ShaderMaterial;
  quad: Mesh;
}

function createShadowMapHudState(): ShadowMapHudState {
  const scene = new Scene();
  const camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  const material = new ShaderMaterial({
    uniforms: {
      tShadow: { value: null },
      flipY: { value: 1 },
      invert: { value: 1 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tShadow;
      uniform int flipY;
      uniform int invert;
      varying vec2 vUv;

      void main() {
        vec2 uv = vUv;
        if (flipY == 1) {
          uv.y = 1.0 - uv.y;
        }
        float depth = texture2D(tShadow, uv).r;
        float value = invert == 1 ? 1.0 - depth : depth;
        value = pow(clamp(value, 0.0, 1.0), 0.5);
        gl_FragColor = vec4(vec3(value), 1.0);
      }
    `,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const quad = new Mesh(new PlaneGeometry(2, 2), material);

  scene.background = new Color(0x111111);
  camera.position.set(0, 0, 1);
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld();
  scene.add(quad);

  return {
    enabled: false,
    size: 220,
    padding: 12,
    flipY: true,
    invert: true,
    scene,
    camera,
    oldViewport: new Vector4(),
    oldScissor: new Vector4(),
    oldClearColor: new Color(),
    material,
    quad,
  };
}

export class THREEViewerRenderer extends THREEViewerRendererBase {
  readonly ssfxParamsManager: SSFXRendererParamsManager | undefined;
  private readonly screenRenderTarget: SSFXRenderTarget | undefined;
  private readonly screenLightweightRenderTarget: SSFXLightweightRenderTarget | undefined;
  private readonly renderTargetPool: RenderTargetPool;
  get hasSSFXRenderer(): boolean {
    return this.screenRenderTarget !== undefined && this.ssfxParamsManager !== undefined;
  }
  gui: dat.GUI | undefined;

  private stats: Stats | undefined;

  private guiEnabledInternal = false;
  get guiEnabled(): boolean {
    return this.guiEnabledInternal;
  }
  set guiEnabled(value: boolean) {
    if (this.guiEnabledInternal === value) {
      return;
    }
    this.guiEnabledInternal = value;
    if (this.guiEnabledInternal) {
      this.guiPromise = this.initGUI();
    } else {
      if (this.gui) {
        this.gui.destroy();
        this.container?.removeChild(this.gui.domElement);
        this.gui = undefined;
      }
    }
  }
  guiPromise: Promise<void> | undefined;

  readonly services: THREEViewerServices;

  readonly materialFactoryGUI: boolean;

  private activePerformanceTier: PerformanceTier | null = null;

  private resourceTrackerLogInterval: number | undefined;

  private lookAtControlsInternal: boolean = false;

  private sectionTransformControls: TransformControls | undefined;

  private readonly envMapHud: EnvironmentHudState = createEnvironmentHudState();
  private readonly shadowMapHud: ShadowMapHudState = createShadowMapHudState();

  get lookAtControls(): boolean {
    return this.lookAtControlsInternal;
  }

  set lookAtControls(value: boolean) {
    if (this.lookAtControlsInternal === value) {
      return;
    }
    this.lookAtControlsInternal = value;
    if (this.viewer.controls && "lookAtControls" in this.viewer.controls) {
      this.viewer.controls.lookAtControls = value;
    }
  }

  private keydownHandler: ((event: KeyboardEvent) => void) | undefined;

  constructor({
    options,
    container,
    viewer,
    sharedWebGL,
    pixelRatio,
  }: {
    options: THREEViewerOptions;
    container: HTMLElement | null | undefined;
    viewer: THREEViewerBase;
    sharedWebGL?: SharedWebGL | null;
    pixelRatio: number; // Pixel ratio used to render the viewer.
  }) {
    function initWebGLContextOrUseShared(): SharedWebGL {
      if (sharedWebGL) {
        return sharedWebGL; // Use the WebGL context from the shared canvas element.
      }
      // Initialize a WebGL context that's shared between this viewer and offscreen renderers. This should only be used on pages that only contain one interactive viewer.
      const webGLContext = initSharedWebGL({
        throwOnGLError: options.throwOnGLError,
        logGLResourceUsage: options.logGLResourceUsage,
        reuse: WebGLContextReuse.AUTO,
      });
      container?.appendChild(webGLContext.canvas);
      return webGLContext;
    }

    const { canvas, renderer, services } = initWebGLContextOrUseShared();
    const { ssfxParamsManager, screenRenderTarget, screenLightweightRenderTarget, renderTargetPool } =
      initPhysicallyBasedRenderer({
        renderer,
        capabilities: services.capabilities,
        userOptions: {
          ssfxRenderer: options.ssfxRenderer,
          useMSAA: options.useMSAA,
        },
        markRendererForUpdate: () => this.markRendererForUpdate(),
      });
    if (!sharedWebGL) {
      // Needed for cleanup around fast refresh.
      renderer.domElement.classList.add(HIGHARC_VIEWER_REINIT_CLASS);
      (renderer.domElement as any).higharcCleanup = () => {
        productLog("Cleaning up THREEViewerRenderer and losing context");
        viewer.removeContextLostListener();
        WebGLMultiContextResourceTracker.removeContext(renderer.getContext());
        renderer.forceContextLoss();
        viewer.dispose();
      };
    }

    // Always set useMSAA to true - MSAAResolver will only be active in case the renderer is in MSAA mode.
    super({ container, viewer, renderer, canvas, sharedWebGL, pixelRatio, useMSAA: true });

    this.materialFactoryGUI = options.materialFactoryGUI;
    this.ssfxParamsManager = ssfxParamsManager;
    this.screenRenderTarget = screenRenderTarget;
    this.screenLightweightRenderTarget = screenLightweightRenderTarget;
    this.renderTargetPool = renderTargetPool;
    this.services = services;

    // Add keyboard event listener
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.code === "KeyP") {
        this.lookAtControls = !this.lookAtControls;
        this.markRendererForUpdate();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    // Store the handler for cleanup
    this.keydownHandler = handleKeyDown;

    (window as any).HIGHARC_THREE_VIEWER_FORCE_LOSE_CONTEXT = () => this.renderer.forceContextLoss();
  }

  override dispose(): boolean {
    if (!super.dispose()) {
      return false;
    }

    // Remove keyboard event listener
    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
      this.keydownHandler = undefined;
    }

    this.screenRenderTarget?.dispose();
    this.screenLightweightRenderTarget?.dispose();
    this.renderTargetPool.dispose();
    if (this.gui) {
      this.container?.removeChild(this.gui.domElement);
      this.gui.destroy();
    }
    if (this.resourceTrackerLogInterval !== undefined) {
      window.clearInterval(this.resourceTrackerLogInterval);
    }
    this.shadowMapHud.quad.geometry.dispose();
    this.shadowMapHud.material.dispose();
    this.stats?.dom.parentElement?.removeChild(this.stats.dom);
    this.envMapHud.sphere.geometry.dispose();
    this.envMapHud.sphereMaterial.dispose();
    return true;
  }

  /**
   * This is intended for measuring performance of long-running operations, like compiling shaders.
   * It's not as well suited for measuring the cost of rendering a single frame, since it will synchronize the pipeline between the CPU and the GPU.
   */
  forceRenderAndFinish() {
    this.markRendererForUpdate();
    this.updateBackbuffer();
    this.refreshCanvas();
    this.renderer.getContext().finish();
  }

  protected override setRendererSize(renderPixelWidth: number, renderPixelHeight: number) {
    if (!this.sharedWebGL && !this.services.onScreenRenderingDisabled) {
      this.renderer.setSize(renderPixelWidth, renderPixelHeight, false);
    }
    this.screenRenderTarget?.setFinalRenderSize(renderPixelWidth, renderPixelHeight);
    this.screenLightweightRenderTarget?.setFinalRenderSize(renderPixelWidth, renderPixelHeight);
  }

  protected override needsMoreTemporalSamples(): boolean {
    // Should return true while TAA is accumulating samples for a fully resolved antialiased image.
    if (this.ssfxParamsManager && this.ssfxParamsManager.screenRenderParams.useLightweightPipeline) {
      return false;
    }
    return !(this.screenRenderTarget?.isDoneAccumulatingSamples() ?? true);
  }

  protected override updateBackbufferInternal(updateRequested: boolean) {
    if (this.stats) {
      this.stats.begin();
    }

    if (this.ssfxParamsManager && this.viewer.scene.ssfxRendererScene) {
      const lightProbeGenerationParams = this.ssfxParamsManager.lightProbeGenerationParams;
      const renderAfterEveryProbe =
        this.ssfxParamsManager.lightProbeRenderParams.lightProbeVisualization !== LightProbeVisualizationMode.None;
      lightProbeGenerationParams.lightProbeFar = this.viewer.camera.far;
      this.viewer.scene.ssfxRendererScene.lightProbeRenderStep(
        this.renderer,
        lightProbeGenerationParams,
        renderAfterEveryProbe,
      );

      this.ssfxParamsManager.setupParamsForRender(this.viewer.scene.ssfxRendererScene, this.viewer.camera);

      const pipeline = this.ssfxParamsManager.screenRenderParams.useLightweightPipeline
        ? this.screenLightweightRenderTarget
        : this.screenRenderTarget;
      if (pipeline) {
        pipeline.setHighlightDataTexture(this.viewer.highlightDataTexture);
        pipeline.setScene(this.viewer.scene.ssfxRendererScene);
        pipeline.setCamera(this.viewer.camera);
        if (updateRequested) {
          const { didUpdateRenderResolution } = pipeline.beginFrame(this.ssfxParamsManager.screenRenderParams);
          this.msaaResolver.setSize(pipeline.sampleRenderTargetWidth, pipeline.sampleRenderTargetHeight);
          if (didUpdateRenderResolution) {
            this.renderTargetPool.setSize(pipeline.sampleRenderTargetWidth, pipeline.sampleRenderTargetHeight);
          }
        }
      }

      if (this.screenRenderTarget && !this.ssfxParamsManager.screenRenderParams.useLightweightPipeline) {
        if (
          !this.ssfxParamsManager.screenRenderParams.ssaaEnabled ||
          !this.screenRenderTarget.isDoneAccumulatingSamples()
        ) {
          this.viewer.scene.ssfxRendererScene.updateShadowMap(this.renderer, this.viewer.camera);
          this.screenRenderTarget.applyRenderParams(this.ssfxParamsManager.getRenderParams());
        }
        if (this.ssfxParamsManager.screenRenderParams.ssaaEnabled) {
          const ssaaSamplesPerFrame = this.ssfxParamsManager.screenRenderParams.ssaaSamplesPerFrame;
          for (let frameIndex = 0; frameIndex < ssaaSamplesPerFrame; ++frameIndex) {
            this.screenRenderTarget.accumulateSampleIfNeeded();
          }
        } else {
          this.screenRenderTarget.renderOneSample(this.msaaResolver.msaaBuffer);
        }
      }
      if (this.screenLightweightRenderTarget && this.ssfxParamsManager.screenRenderParams.useLightweightPipeline) {
        this.viewer.scene.ssfxRendererScene.updateShadowMap(this.renderer, this.viewer.camera);
        this.screenLightweightRenderTarget.applyRenderParams(this.ssfxParamsManager.getRenderParams());
        this.screenLightweightRenderTarget.renderOneSample(this.msaaResolver.msaaBuffer);
      }
    } else {
      this.updateBackbufferWithoutSSFXRenderer(Math.random());
    }

    if (this.stats) {
      this.stats.end();
    }
  }

  override refreshCanvas() {
    if (this.services.onScreenRenderingDisabled) {
      return;
    }
    this.refreshSectionTransformControls();
    this.renderer.setRenderTarget(null);
    const { valid, aabb } = this.getCanvasPixelAABB();
    if (!valid) {
      return;
    }
    if (this.suspenders.size > 0) {
      this.clearQuad(aabb, 0xffffff, 1);
      return;
    }
    if (
      this.screenRenderTarget &&
      this.ssfxParamsManager &&
      this.ssfxParamsManager.screenRenderParams.ssaaEnabled &&
      !this.ssfxParamsManager.screenRenderParams.useLightweightPipeline
    ) {
      this.screenRenderTarget.renderSSAAOutput(null, aabb);
    } else {
      if (
        this.screenLightweightRenderTarget &&
        this.ssfxParamsManager &&
        this.ssfxParamsManager.screenRenderParams.useLightweightPipeline &&
        !this.screenLightweightRenderTarget.setupSupported
      ) {
        this.clearQuad(aabb, 0xff00ff, 1);
        return;
      }
      this.msaaResolver.resolve(this.renderer, aabb);
    }
    this.renderEnvironmentHud(aabb);
    this.renderShadowMapHud(aabb);
  }

  private renderEnvironmentHud(viewerAabbOnCanvas: AABB2) {
    const { envMapHud } = this;

    if (!envMapHud.enabled) {
      return;
    }

    const environment = this.viewer.scene.ssfxRendererScene?.environment;
    if (environment === undefined || environment === null) {
      return;
    }

    const aabbWidth = viewerAabbOnCanvas.maxX - viewerAabbOnCanvas.minX;
    const aabbHeight = viewerAabbOnCanvas.maxY - viewerAabbOnCanvas.minY;
    const maxHudSize = Math.min(aabbWidth, aabbHeight) - envMapHud.padding * 2;
    if (maxHudSize < 32) {
      return;
    }
    const hudSize = Math.min(envMapHud.size, maxHudSize);

    const viewportX = viewerAabbOnCanvas.minX + envMapHud.padding;
    const viewportY = viewerAabbOnCanvas.maxY - envMapHud.padding - hudSize;

    this.renderer.getViewport(envMapHud.oldViewport);
    this.renderer.getScissor(envMapHud.oldScissor);
    const oldScissorTest = this.renderer.getScissorTest();
    const oldAutoClear = this.renderer.autoClear;
    this.renderer.getClearColor(envMapHud.oldClearColor);
    const oldClearAlpha = this.renderer.getClearAlpha();

    if (envMapHud.scene.environment !== environment) {
      envMapHud.scene.environment = environment;
    }
    if (envMapHud.mode === "background") {
      if (envMapHud.scene.background !== environment) {
        envMapHud.scene.background = environment;
      }
      envMapHud.sphere.visible = false;
    } else {
      if (envMapHud.scene.background !== envMapHud.darkBackground) {
        envMapHud.scene.background = envMapHud.darkBackground;
      }
      envMapHud.sphere.visible = true;
    }

    if (envMapHud.useViewerAngle) {
      this.viewer.camera.getWorldDirection(envMapHud.cameraDirection);
      envMapHud.camera.position.copy(envMapHud.cameraDirection).multiplyScalar(-1.35);
      envMapHud.cameraUp.set(0, 1, 0).applyQuaternion(this.viewer.camera.quaternion).normalize();
      envMapHud.camera.up.copy(envMapHud.cameraUp);
      envMapHud.camera.lookAt(0, 0, 0);
    } else {
      const yawRad = MathUtils.degToRad(envMapHud.yaw);
      const pitchRad = MathUtils.degToRad(envMapHud.pitch);
      envMapHud.cameraDirection.set(
        -Math.cos(pitchRad) * Math.sin(yawRad),
        Math.sin(pitchRad),
        -Math.cos(pitchRad) * Math.cos(yawRad),
      );
      envMapHud.camera.position.copy(envMapHud.cameraDirection).multiplyScalar(-1.35);
      envMapHud.camera.up.set(0, 1, 0);
      envMapHud.camera.lookAt(0, 0, 0);
    }
    envMapHud.camera.updateMatrixWorld();

    this.renderer.autoClear = false;
    this.renderer.setViewport(viewportX, viewportY, hudSize, hudSize);
    // Scissor keeps the HUD clear and background confined to the overlay viewport.
    this.renderer.setScissor(viewportX, viewportY, hudSize, hudSize);
    this.renderer.setScissorTest(true);
    this.renderer.setClearColor(0x111111, 1);
    this.renderer.clear(true, true, true);
    this.renderer.render(envMapHud.scene, envMapHud.camera);

    this.renderer.setViewport(envMapHud.oldViewport);
    this.renderer.setScissor(envMapHud.oldScissor);
    this.renderer.setScissorTest(oldScissorTest);
    this.renderer.autoClear = oldAutoClear;
    this.renderer.setClearColor(envMapHud.oldClearColor, oldClearAlpha);
  }

  private renderShadowMapHud(viewerAabbOnCanvas: AABB2) {
    const { shadowMapHud } = this;

    if (!shadowMapHud.enabled) {
      return;
    }
    const ssfxRendererScene = this.viewer.scene.ssfxRendererScene;
    const shadowMap = ssfxRendererScene?.sceneLightsManager.directionalLight.shadow.map;
    if (!shadowMap) {
      return;
    }

    const shadowTexture = shadowMap.depthTexture ?? shadowMap.texture;
    if (!shadowTexture) {
      return;
    }

    const aabbWidth = viewerAabbOnCanvas.maxX - viewerAabbOnCanvas.minX;
    const aabbHeight = viewerAabbOnCanvas.maxY - viewerAabbOnCanvas.minY;
    const maxHudSize = Math.min(aabbWidth, aabbHeight) - shadowMapHud.padding * 2;
    if (maxHudSize < 64) {
      return;
    }
    const hudSize = Math.max(64, Math.min(shadowMapHud.size, maxHudSize));

    const viewportX = viewerAabbOnCanvas.minX + shadowMapHud.padding;
    const viewportY = viewerAabbOnCanvas.minY + shadowMapHud.padding;

    this.renderer.getViewport(shadowMapHud.oldViewport);
    this.renderer.getScissor(shadowMapHud.oldScissor);
    const oldScissorTest = this.renderer.getScissorTest();
    const oldAutoClear = this.renderer.autoClear;
    this.renderer.getClearColor(shadowMapHud.oldClearColor);
    const oldClearAlpha = this.renderer.getClearAlpha();

    shadowMapHud.material.uniforms.tShadow.value = shadowTexture;
    shadowMapHud.material.uniforms.flipY.value = shadowMapHud.flipY ? 1 : 0;
    shadowMapHud.material.uniforms.invert.value = shadowMapHud.invert ? 1 : 0;

    this.renderer.autoClear = false;
    this.renderer.setViewport(viewportX, viewportY, hudSize, hudSize);
    // Scissor keeps the HUD scene background from affecting the rest of the frame.
    this.renderer.setScissor(viewportX, viewportY, hudSize, hudSize);
    this.renderer.setScissorTest(true);
    this.renderer.render(shadowMapHud.scene, shadowMapHud.camera);

    this.renderer.setViewport(shadowMapHud.oldViewport);
    this.renderer.setScissor(shadowMapHud.oldScissor);
    this.renderer.setScissorTest(oldScissorTest);
    this.renderer.autoClear = oldAutoClear;
    this.renderer.setClearColor(shadowMapHud.oldClearColor, oldClearAlpha);
  }

  /**
   * Only for e2e tests! Does not set viewport, render size is determined from canvas size.
   */
  renderTestWithoutSSFXRenderer() {
    // We need to render to the MSAAResolver buffer since it has depth and stencil buffers. The default framebuffer doesn't have them.
    this.msaaResolver.setSize(this.renderer.domElement.width, this.renderer.domElement.height);
    this.updateBackbufferWithoutSSFXRenderer(0.5);
    this.renderer.setRenderTarget(null);
    this.msaaResolver.resolve(this.renderer, undefined);
  }

  private updateBackbufferWithoutSSFXRenderer(randomSeedUniform: number) {
    this.renderer.setRenderTarget(this.msaaResolver.msaaBuffer);
    if (!this.viewer.scene.ssfxRendererScene) {
      return;
    }
    this.viewer.scene.ssfxRendererScene.updateShadowMap(this.renderer, this.viewer.camera);
    this.viewer.scene.ssfxRendererScene.applyToneMappingToSkyMaterial(false, TONE_MAPPING_ALGORITHM);
    const sceneSharedUniforms = this.viewer.scene.ssfxRendererScene.getSceneSharedUniformValues(false);
    const sharedUniformValues: SharedUniformValues = {
      outputType: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS,
      ssfxEnabled: false,

      ...sceneSharedUniforms,
      renderTargetResolution: [this.renderer.domElement.width, this.renderer.domElement.height],
      softShadowCameraFar: SOFT_SHADOW_CAMERA_FAR_DEFAULT,

      // SSFXRenderer is disabled, so these parameters don't have an effect.
      screenSpaceAoMap: null,
      screenSpaceReflectionMap: null,
      screenSpaceShadowMap: null,
      substitutedReflectionMultiplier: 0,
      lightProbeIrradianceMultiplier: 0,
      probeGridNormalOffsetMultiplier: 0,
      probeGridSmoothBackfaceWeightConstantTerm: 0,
      isolateMode: this.viewer.highlightDataTexture?.isolateMode ?? false,
    };
    prepareMaterialsForRender(this.viewer.highlightDataTexture);
    MaterialSharedParameterGroup.instance.setSharedUniformValues(sharedUniformValues);
    MaterialSharedParameterGroup.instance.randomSeed = randomSeedUniform;
    (this.viewer.scene.scene as ExtendedScene).getUniformsForLayerMask =
      this.viewer.scene.layerSpecificUniforms.simpleGetUniformsForLayerMask;
    this.renderer.render(this.viewer.scene.scene, this.viewer.camera);
  }

  renderImageSync(
    scene: SSFXRendererScene,
    camera: Camera,
    imageOptions: RenderImageOptions,
    renderParams: RendererParams,
    onImageReady?: ImageReadyCallback,
  ): string | undefined {
    this.updateWorldToPixelFactor(camera, [imageOptions.width, imageOptions.height], 1);
    return this.services.imageRenderer.renderImageSync(scene, camera, renderParams, imageOptions, onImageReady);
  }

  renderImageSyncTest(
    scene: SSFXRendererScene,
    camera: Camera,
    imageOptions: RenderImageOptions,
    renderParams: RendererParams,
  ) {
    this.updateWorldToPixelFactor(camera, [imageOptions.width, imageOptions.height], 1);
    this.services.imageRenderer.renderImageSyncTest(scene, camera, renderParams, imageOptions);
  }

  setPerformanceTier(tier: PerformanceTier) {
    productLog("Set performance tier to " + tier.name);

    this.activePerformanceTier = tier;

    this.services.httpMaterialFactory.setBakeColorTexture(
      tier.fetchBakedColorTextures ? BakeColorTextureMode.FETCH_FROM_AWS : BakeColorTextureMode.DONT_BAKE,
    );

    if (this.ssfxParamsManager) {
      this.ssfxParamsManager.setRenderParameters(tier.params);
      this.markRendererForUpdate();
    }
  }

  setDebugFeaturesEnabled(enabled) {
    this.renderer.debug.checkShaderErrors = enabled;
    if (enabled) {
      if (!this.stats && this.canvas.parentElement) {
        this.stats = new Stats();
        this.stats.dom.style.position = "absolute";
        this.canvas.parentElement.appendChild(this.stats.dom);
      }
    }
    if (this.stats) {
      this.stats.dom.style.display = enabled ? "initial" : "none";
    }
  }

  async initGUI() {
    if (!this.gui) {
      const dat = await import("dat.gui");

      if (!this.guiEnabledInternal) {
        return;
      }

      const gui = (this.gui = new dat.GUI({ autoPlace: false, width: 370 }));

      const viewerGui = gui.addFolder("Viewer options");

      if (this.ssfxParamsManager) {
        this.ssfxParamsManager.initGUI(viewerGui);
      }

      if (!this.viewer.scene.ssfxRendererScene) {
        return;
      }

      this.viewer.scene.ssfxRendererScene.sceneLightsManager.initGUI(viewerGui);

      if (this.materialFactoryGUI) {
        this.services.httpMaterialFactory.initGUI(this.gui, () => this.markRendererForUpdate());
      }

      const testGUI = viewerGui.addFolder("Testing");
      testGUI.add(this, "autoRotate" as keyof THREEViewerRenderer).onChange((val: boolean) => {
        this.viewer.controls.autoRotate = val;
        if (this.stats) {
          this.stats.fpsMeasureInterval = val ? 10 : -1;
        }
      });

      const environmentHudGUI = viewerGui.addFolder("Environment HUD");
      const { envMapHud } = this;

      environmentHudGUI
        .add(envMapHud, "enabled")
        .name("enabled")
        .onChange(() => {
          this.markRendererForUpdate();
        });
      environmentHudGUI
        .add(envMapHud, "mode", ["sphere", "background"])
        .name("mode")
        .onChange(() => {
          this.markRendererForUpdate();
        });
      environmentHudGUI
        .add(envMapHud, "size", 128, 512, 1)
        .name("size")
        .onChange(() => {
          this.markRendererForUpdate();
        });
      environmentHudGUI
        .add(envMapHud, "padding", 4, 40, 1)
        .name("padding")
        .onChange(() => {
          this.markRendererForUpdate();
        });
      environmentHudGUI
        .add(envMapHud, "useViewerAngle")
        .name("use viewer angle")
        .onChange(() => {
          this.markRendererForUpdate();
        });
      environmentHudGUI
        .add(envMapHud, "yaw", -180, 180, 1)
        .name("yaw")
        .onChange(() => {
          this.markRendererForUpdate();
        });
      environmentHudGUI
        .add(envMapHud, "pitch", -80, 80, 1)
        .name("pitch")
        .onChange(() => {
          this.markRendererForUpdate();
        });

      const shadowHudGui = viewerGui.addFolder("Shadow Map HUD");
      const { shadowMapHud } = this;

      shadowHudGui.add(shadowMapHud, "enabled").onChange(() => {
        this.markRendererForUpdate();
      });
      shadowHudGui.add(shadowMapHud, "size", 64, 512, 1).onChange(() => {
        this.markRendererForUpdate();
      });
      shadowHudGui.add(shadowMapHud, "padding", 4, 40, 1).onChange(() => {
        this.markRendererForUpdate();
      });
      shadowHudGui.add(shadowMapHud, "flipY").onChange(() => {
        this.markRendererForUpdate();
      });
      shadowHudGui.add(shadowMapHud, "invert").onChange(() => {
        this.markRendererForUpdate();
      });

      if (this.hasSSFXRenderer) {
        const performanceTier = this.activePerformanceTier ?? (await this.services.getPerformanceTierByScore());
        const performanceTierParams = {
          tier: performanceTier.name,
        };

        const tierNames: string[] = [];
        const tiersByName = {};
        for (const tier of PERFORMANCE_TIERS) {
          tierNames.push(tier.name);
          tiersByName[tier.name] = tier;
        }

        const performanceTierGUI = viewerGui.addFolder("Performance tier");
        performanceTierGUI
          .add(performanceTierParams, "tier", tierNames)
          .onChange((val: string) => this.setPerformanceTier(tiersByName[val]));
      }

      gui.domElement.style.position = "absolute";
      gui.domElement.style.top = "0";
      gui.domElement.style.right = "0";
      gui.domElement.style.zIndex = "10000";
    }
    this.container?.appendChild(this.gui.domElement);
  }

  refreshSectionTransformControls() {
    const section = this.viewer.scene.ssfxRendererScene?.getSection();
    if (this.sectionTransformControls && this.ssfxParamsManager) {
      this.sectionTransformControls.setMode(this.ssfxParamsManager.screenRenderParams.sectionTransformControlsMode);
    }
    if (this.sectionTransformControls?.object === section) {
      return;
    }
    if (this.sectionTransformControls) {
      this.sectionTransformControls.detach();
      this.viewer.scene.staticContent.remove(this.sectionTransformControls);
      this.sectionTransformControls = undefined;
    }
    if (
      !section ||
      !this.ssfxParamsManager ||
      !this.ssfxParamsManager.screenRenderParams.sectionTransformControlsEnabled
    ) {
      return;
    }
    const transformControls = new TransformControls(this.viewer.camera, this.viewer.container);
    this.viewer.scene.staticContent.add(transformControls);
    transformControls.setMode("translate");
    transformControls.attach(section);
    transformControls.addEventListener("change", () => {
      if (section) {
        section.updateMatrix();
        section.updateMatrixWorld();
        transformControls.updateMatrixWorld();

        this.viewer.scene.markRenderingNeedsUpdate();
      }
    });
    transformControls.addEventListener("dragging-changed", (event) => {
      // Disable camera controls when interacting with the transform controls.
      if (event.value) {
        this.viewer.controls.lock("ssfx-section-debug-transform-drag");
      } else {
        this.viewer.controls.unlock("ssfx-section-debug-transform-drag");
      }
    });
    this.viewer.controls.addEventListener("change", () => transformControls.updateMatrixWorld());
    this.sectionTransformControls = transformControls;
  }
}
